import { Contact2, Map, Plus, Ticket, Users } from 'lucide-react';

import { Permission, type PermissionString } from '../../../types/permissions';
import type { PaletteCommand } from '../commandPalette.types';

type HasAnyPermission = (...permissions: PermissionString[]) => boolean;

// Each carries the gate of the route it points at, and the whole set belongs
// to admin mode, so no row is a trip to Unauthorized.
const NAV_COMMANDS: (PaletteCommand & { requiresAnyOf?: PermissionString[] })[] = [
  {
    id: 'customers',
    label: 'Go to Customers',
    icon: Contact2,
    hint: 'Section',
    keywords: ['customer', 'customers', 'people'],
    to: '/admin/customers',
    requiresAnyOf: [Permission.READ_CUSTOMERS, Permission.READ_ALL],
  },
  {
    id: 'venues',
    label: 'Go to Venues',
    icon: Map,
    hint: 'Section',
    keywords: ['venue', 'venues', 'location', 'locations'],
    to: '/admin/venues',
    requiresAnyOf: [Permission.READ_VENUES, Permission.WRITE_VENUES, Permission.READ_ALL],
  },
  // `/admin/staff` carries no route guard, so there is nothing here to mirror.
  {
    id: 'staff',
    label: 'Go to Staff',
    icon: Users,
    hint: 'Section',
    keywords: ['staff', 'teacher', 'teachers', 'instructor', 'instructors'],
    to: '/admin/staff',
  },
  {
    id: 'bookings',
    label: 'Go to Bookings',
    icon: Ticket,
    hint: 'Section',
    keywords: ['booking', 'bookings', 'ticket', 'tickets', 'order', 'orders'],
    to: '/admin/bookings',
    requiresAnyOf: [Permission.READ_BOOKINGS, Permission.READ_ALL],
  },
];

export interface CreateActions {
  workshop: { allowed: boolean; open: () => void; shortcut: readonly string[] };
  booking: { allowed: boolean; open: () => void; shortcut: readonly string[] };
  venue: { allowed: boolean; open: () => void; shortcut: readonly string[] };
}

/** The "Create" commands the viewer may run, each gated by its own launcher. */
function createCommands(actions: CreateActions): PaletteCommand[] {
  const commands: PaletteCommand[] = [];
  if (actions.workshop.allowed) {
    commands.push({
      id: 'new-workshop',
      label: 'New workshop',
      icon: Plus,
      hint: 'Create',
      keywords: ['workshop', 'workshops', 'create', 'add', 'schedule'],
      shortcut: actions.workshop.shortcut,
      onSelect: actions.workshop.open,
    });
  }
  if (actions.booking.allowed) {
    commands.push({
      id: 'new-booking',
      label: 'New booking',
      icon: Plus,
      hint: 'Create',
      keywords: ['booking', 'bookings', 'create', 'add', 'comp', 'ticket'],
      shortcut: actions.booking.shortcut,
      onSelect: actions.booking.open,
    });
  }
  if (actions.venue.allowed) {
    commands.push({
      id: 'new-venue',
      label: 'New venue',
      icon: Plus,
      hint: 'Create',
      keywords: ['venue', 'venues', 'create', 'add', 'location'],
      shortcut: actions.venue.shortcut,
      onSelect: actions.venue.open,
    });
  }
  return commands;
}

/** Destinations this viewer may actually reach: none outside admin mode,
 *  and none whose route their grants would refuse. */
function destinationsFor(mode: string, hasAnyPermission: HasAnyPermission): PaletteCommand[] {
  if (mode !== 'admin') return [];
  return NAV_COMMANDS.filter((c) => !c.requiresAnyOf || hasAnyPermission(...c.requiresAnyOf));
}

export function availableCommands(
  actions: CreateActions,
  mode: string,
  hasAnyPermission: HasAnyPermission,
): PaletteCommand[] {
  return [...createCommands(actions), ...destinationsFor(mode, hasAnyPermission)];
}

/** Every typed word must appear in the label or start one of the keywords. */
export function commandMatches(command: PaletteCommand, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const label = command.label.toLowerCase();
  const keywords = command.keywords ?? [];
  return words.every(
    (word) => label.includes(word) || keywords.some((keyword) => keyword.startsWith(word)),
  );
}
